import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Download, FileText, Calendar, TrendingUp, TrendingDown,
  Wallet, Receipt, AlertCircle, CheckCircle, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentLedgerPage = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [ledgerData, setLedgerData] = useState(null);
  const [dateFilter, setDateFilter] = useState({
    start: '',
    end: ''
  });

  useEffect(() => {
    if (studentId) {
      fetchLedger();
    }
  }, [studentId]);

  const fetchLedger = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/fee-management/student/${studentId}/ledger`;
      const params = new URLSearchParams();
      if (dateFilter.start) params.append('start_date', dateFilter.start);
      if (dateFilter.end) params.append('end_date', dateFilter.end);
      if (params.toString()) url += `?${params}`;
      
      const res = await axios.get(url, { headers: getAuthHeader() });
      setLedgerData(res.data);
    } catch (error) {
      toast.error('Failed to load ledger');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      toast.loading('Generating PDF...');
      let url = `${API_URL}/api/fee-management/student/${studentId}/ledger/pdf`;
      const params = new URLSearchParams();
      if (dateFilter.start) params.append('start_date', dateFilter.start);
      if (dateFilter.end) params.append('end_date', dateFilter.end);
      if (params.toString()) url += `?${params}`;
      
      const response = await axios.get(url, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Ledger_${ledgerData?.student?.name || studentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast.dismiss();
      toast.success('Ledger PDF downloaded!');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to download PDF');
    }
  };

  const applyFilter = () => {
    fetchLedger();
  };

  const clearFilter = () => {
    setDateFilter({ start: '', end: '' });
    setTimeout(fetchLedger, 100);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout title="Student Ledger">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const student = ledgerData?.student || {};
  const summary = ledgerData?.summary || {};
  const entries = ledgerData?.entries || [];

  return (
    <DashboardLayout title="Student Ledger">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{student.name}</h1>
              <p className="text-muted-foreground">
                {student.admission_number} • {student.class_name}
              </p>
            </div>
          </div>
          <Button onClick={downloadPDF} data-testid="download-ledger-pdf">
            <Download className="h-4 w-4 mr-2" /> Download PDF
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Charges</p>
                  <p className="text-lg font-bold">{formatCurrency(summary.total_charges)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Late Fines</p>
                  <p className="text-lg font-bold">{formatCurrency(summary.total_fines)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Receipt className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Discounts</p>
                  <p className="text-lg font-bold">{formatCurrency(summary.total_discounts)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <TrendingDown className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Paid</p>
                  <p className="text-lg font-bold">{formatCurrency(summary.total_payments)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={summary.current_balance > 0 ? "border-red-500 bg-red-50 dark:bg-red-950/30" : "border-green-500 bg-green-50 dark:bg-green-950/30"}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${summary.current_balance > 0 ? 'bg-red-100 dark:bg-red-900/50' : 'bg-green-100 dark:bg-green-900/50'}`}>
                  <Wallet className={`h-5 w-5 ${summary.current_balance > 0 ? 'text-red-600' : 'text-green-600'}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Current Balance</p>
                  <p className={`text-xl font-bold ${summary.current_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(summary.current_balance)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Date Filter */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Filter by Date</span>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  value={dateFilter.start}
                  onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
                  className="w-40"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  value={dateFilter.end}
                  onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
                  className="w-40"
                />
              </div>
              <Button onClick={applyFilter} size="sm">
                Apply
              </Button>
              <Button onClick={clearFilter} variant="outline" size="sm">
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Ledger Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Transaction Ledger
            </CardTitle>
            <CardDescription>
              {entries.length} transactions found
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium">Date</th>
                    <th className="p-3 text-left text-sm font-medium">Description</th>
                    <th className="p-3 text-left text-sm font-medium">Reference</th>
                    <th className="p-3 text-right text-sm font-medium">Debit (₹)</th>
                    <th className="p-3 text-right text-sm font-medium">Credit (₹)</th>
                    <th className="p-3 text-right text-sm font-medium">Balance (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-muted-foreground">
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    entries.map((entry, idx) => (
                      <tr key={idx} className={`border-t ${entry.type === 'credit' ? 'bg-green-50/50 dark:bg-green-950/20' : ''}`}>
                        <td className="p-3 text-sm">{entry.date}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {entry.category === 'payment' && <CheckCircle className="h-4 w-4 text-green-500" />}
                            {entry.category === 'late_fine' && <AlertCircle className="h-4 w-4 text-orange-500" />}
                            {entry.category === 'discount' && <Receipt className="h-4 w-4 text-purple-500" />}
                            <span className="text-sm">{entry.description}</span>
                          </div>
                        </td>
                        <td className="p-3 text-sm font-mono text-muted-foreground">{entry.reference}</td>
                        <td className="p-3 text-sm text-right font-mono text-red-600">
                          {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                        </td>
                        <td className="p-3 text-sm text-right font-mono text-green-600">
                          {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                        </td>
                        <td className={`p-3 text-sm text-right font-mono font-bold ${entry.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(entry.balance)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {entries.length > 0 && (
                  <tfoot className="bg-slate-100 dark:bg-slate-800">
                    <tr>
                      <td colSpan={3} className="p-3 text-right font-bold">Totals:</td>
                      <td className="p-3 text-right font-mono font-bold text-red-600">
                        {formatCurrency(summary.total_charges + summary.total_fines)}
                      </td>
                      <td className="p-3 text-right font-mono font-bold text-green-600">
                        {formatCurrency(summary.total_payments + summary.total_discounts)}
                      </td>
                      <td className={`p-3 text-right font-mono font-bold ${summary.current_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(summary.current_balance)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Status Badge */}
        <div className="flex justify-center">
          {summary.current_balance > 0 ? (
            <Badge variant="destructive" className="text-lg px-6 py-2">
              <AlertCircle className="h-5 w-5 mr-2" />
              Amount Due: {formatCurrency(summary.current_balance)}
            </Badge>
          ) : (
            <Badge className="bg-green-600 text-lg px-6 py-2">
              <CheckCircle className="h-5 w-5 mr-2" />
              All Fees Cleared
            </Badge>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentLedgerPage;
